<template>
  <woot-modal :show.sync="show" :on-close="onClose">
    <div class="h-auto overflow-auto flex flex-col">
      <woot-modal-header header-title="Adicionar Pipeline" />

      <form class="flex w-full" @submit.prevent="addAttributes">
        <div class="w-full">
          <woot-input
            v-model="displayName"
            label="Nome da Pipeline"
            type="text"
            :class="{ error: $v.displayName.$error }"
            :error="$v.displayName.$error ? 'Nome é obrigatório' : ''"
            placeholder="Digite o nome da pipeline"
            @input="onDisplayNameChange"
            @blur="$v.displayName.$touch"
          />
          <woot-input
            v-model="attributeKey"
            label="Chave da Pipeline"
            type="text"
            :class="{ error: $v.attributeKey.$error }"
            :error="$v.attributeKey.$error ? keyErrorMessage : ''"
            placeholder="Digite a chave da pipeline"
            @blur="$v.attributeKey.$touch"
          />
          <label :class="{ error: $v.description.$error }">
            Descrição
            <textarea
              v-model="description"
              rows="3"
              type="text"
              placeholder="Digite a descrição da pipeline"
              @blur="$v.description.$touch"
            />
            <span v-if="$v.description.$error" class="message">
              Descrição é obrigatória
            </span>
          </label>
          <label :class="{ error: $v.attributeType.$error }">
            Etapas
            <select v-model="attributeType" disabled>
              <option :value="6">Lista</option>
            </select>
            <span v-if="$v.attributeType.$error" class="message">
              Etapas são obrigatórias
            </span>
          </label>
          <div class="multiselect--wrap">
            <label> Etapas </label>
            <multiselect
              ref="tagInput"
              v-model="values"
              placeholder="Digite as etapas da sua pipeline e pressione enter"
              label="name"
              track-by="name"
              :class="{ invalid: isMultiselectInvalid }"
              :options="options"
              :multiple="true"
              :taggable="true"
              @close="onTouch"
              @tag="addTagValue"
            />
            <label v-show="isMultiselectInvalid" class="error-message">
              Deve ter pelo menos uma etapa
            </label>
          </div>
          <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
            <woot-submit-button
              :disabled="isButtonDisabled"
              button-text="Criar"
            />
            <button class="button clear" @click.prevent="onClose">
              Cancelar
            </button>
          </div>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<script>
import { required, minLength } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
import { convertToAttributeSlug } from 'dashboard/helper/commons.js';
import alertMixin from 'shared/mixins/alertMixin';

export default {
  mixins: [alertMixin],
  data() {
    return {
      displayName: '',
      description: '',
      attributeModel: 0,
      attributeType: 6, // Fixed value for "Lista"
      attributeKey: '',
      models: [],
      types: [],
      values: [],
      options: [],
      show: true,
      isTouched: false,
    };
  },

  computed: {
    ...mapGetters({
      uiFlags: 'getUIFlags',
    }),
    isMultiselectInvalid() {
      return this.isTouched && this.values.length === 0;
    },
    isTagInputInvalid() {
      return this.isAttributeTypeList && this.values.length === 0;
    },
    attributeListValues() {
      return this.values.map(item => item.name);
    },
    isButtonDisabled() {
      return (
        this.$v.displayName.$invalid ||
        this.$v.description.$invalid ||
        this.uiFlags.isCreating ||
        this.isTagInputInvalid
      );
    },
    keyErrorMessage() {
      if (!this.$v.attributeKey.isKey) {
        return 'Chave inválida';
      }
      return 'Chave é obrigatória';
    },
    isAttributeTypeList() {
      return this.attributeType === 6;
    },
  },

  validations: {
    displayName: {
      required,
      minLength: minLength(1),
    },
    description: {
      required,
    },
    attributeModel: {
      required,
    },
    attributeType: {
      required,
    },
    attributeKey: {
      required,
      isKey(value) {
        return !(value.indexOf(' ') >= 0);
      },
    },
  },

  methods: {
    onClose() {
      this.$emit('on-close');
    },
    addTagValue(tagValue) {
      const tag = {
        name: tagValue,
      };
      this.values.push(tag);
      this.$refs.tagInput.$el.focus();
    },
    onTouch() {
      this.isTouched = true;
    },
    onDisplayNameChange() {
      this.attributeKey = convertToAttributeSlug(this.displayName);
    },
    async addAttributes() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      try {
        await this.$store.dispatch('attributes/create', {
          attribute_display_name: this.displayName,
          attribute_description: this.description,
          attribute_model: this.attributeModel,
          attribute_display_type: this.attributeType,
          attribute_key: this.attributeKey,
          attribute_values: this.attributeListValues,
        });
        this.alertMessage = 'Pipeline adicionada com sucesso!';
        this.onClose();
      } catch (error) {
        const errorMessage = error?.message;
        this.alertMessage =
          errorMessage ||
          'Não foi possível criar a pipeline. Por favor, tente novamente mais tarde.';
      } finally {
        this.showAlert(this.alertMessage);
        window.location.reload();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.key-value {
  padding: 0 var(--space-small) var(--space-small) 0;
  font-family: monospace;
}
.multiselect--wrap {
  margin-bottom: var(--space-normal);
  .error-message {
    color: var(--r-400);
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-normal);
  }
  .invalid {
    ::v-deep {
      .multiselect__tags {
        border: 1px solid var(--r-400);
      }
    }
  }
}
::v-deep {
  .multiselect {
    margin-bottom: 0;
  }
  .multiselect__content-wrapper {
    display: none;
  }
  .multiselect--active .multiselect__tags {
    border-radius: var(--border-radius-normal);
  }
}
</style>
